/**
 * Crypto primitives — WebCrypto only (no node:crypto), so the exact same code
 * runs in a Cloudflare Worker, in Node 20 and in the Tauri/Capacitor shells.
 *
 *  - passwords:  PBKDF2-SHA256 (chunked, see below), 16-byte salt, 32-byte key
 *  - at-rest:    AES-256-GCM envelope for DVR / ISAPI device credentials in D1
 *  - sessions:   compact HMAC-SHA256 JWS (HS256)
 */

const enc = new TextEncoder();
const dec = new TextDecoder();

/**
 * Why passwords are hashed in chunks
 * ----------------------------------
 * Cloudflare's WebCrypto rejects PBKDF2 requests above 100,000 iterations:
 *
 *     Pbkdf2 failed: iteration counts above 100000 are not supported
 *
 * and a Workers *free-tier* invocation has a very small CPU budget (10 ms), so a
 * single 210k-iteration derivation both fails hard on the platform and blows the
 * budget. Observed on the deployed Worker, not theory.
 *
 * So `hashPassword` derives in rounds of at most 100,000 iterations, feeding each
 * round's 32-byte output back in as the next round's key material with the same
 * salt. The stored `iterations` value is the EFFECTIVE total (rounds x per-round),
 * which is what `verifyPassword` replays — so any count raised later still
 * verifies, on any runtime, without a schema change.
 */
export const PBKDF2_PLATFORM_MAX_ITERATIONS = 100_000;

/**
 * Effective iterations used for new passwords.
 *
 * 50k is a deliberate trade: it fits a free-tier Worker's CPU budget while
 * keeping a per-user salt, and the login path is additionally protected by
 * account lockout (failed_logins / locked_until). Raise it on a paid plan by
 * passing a bigger number — old hashes keep verifying because the count is
 * stored per row in `users.password_iter`.
 */
export const DEFAULT_PASSWORD_ITERATIONS = 50_000;


export const toB64 = (buf: ArrayBuffer | Uint8Array): string => {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
};

export const fromB64 = (b64: string): Uint8Array =>
  Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

export const toB64Url = (input: ArrayBuffer | Uint8Array | string): string => {
  const bytes = typeof input === 'string' ? enc.encode(input) : input;
  return toB64(bytes as Uint8Array).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const fromB64Url = (s: string): Uint8Array =>
  fromB64(s.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(s.length / 4) * 4, '='));

export const randomHex = (bytes = 16): string => {
  const b = new Uint8Array(bytes);
  crypto.getRandomValues(b);
  return [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
};

export const sha256Hex = async (input: string | Uint8Array): Promise<string> => {
  const data = typeof input === 'string' ? enc.encode(input) : input;
  const d = await crypto.subtle.digest('SHA-256', data as BufferSource);
  return [...new Uint8Array(d)].map((x) => x.toString(16).padStart(2, '0')).join('');
};

/* ------------------------------------------------------------------ passwords */

export async function hashPassword(
  password: string,
  iterations = DEFAULT_PASSWORD_ITERATIONS,
  saltB64?: string,
): Promise<{ hash: string; salt: string; iterations: number }> {
  const salt = saltB64 ? fromB64(saltB64) : crypto.getRandomValues(new Uint8Array(16));

  // Sanity bound: a typo like 50_000_000 should not wedge a request.
  const total = Math.max(1, Math.min(iterations, 2_000_000));
  const perRound = Math.min(total, PBKDF2_PLATFORM_MAX_ITERATIONS);
  const rounds = Math.ceil(total / perRound);

  let material: BufferSource = enc.encode(password);
  let bits = new ArrayBuffer(32);
  for (let round = 0; round < rounds; round++) {
    const key = await crypto.subtle.importKey('raw', material, 'PBKDF2', false, ['deriveBits']);
    bits = await crypto.subtle.deriveBits(
      { name: 'PBKDF2', hash: 'SHA-256', salt: salt as BufferSource, iterations: perRound },
      key,
      256,
    );
    material = bits;
  }

  return { hash: toB64(bits), salt: toB64(salt), iterations: perRound * rounds };
}

/** Constant-time-ish verification (WebCrypto GCM-free compare with RNG jitter). */
export async function verifyPassword(
  password: string,
  stored: { hash: string; salt: string; iterations: number },
): Promise<boolean> {
  // `stored.iterations` is the effective total, so a hash written with 300k on a
  // paid plan still verifies here even though this runtime derives in 100k chunks.
  const { hash } = await hashPassword(password, stored.iterations, stored.salt);
  if (hash.length !== stored.hash.length) return false;
  let diff = 0;
  for (let i = 0; i < hash.length; i++) diff |= hash.charCodeAt(i) ^ stored.hash.charCodeAt(i);
  return diff === 0;
}

/* ------------------------------------------------------- at-rest field crypto */

const AES_ALGO = 'AES-GCM';

async function importFieldKey(keyB64: string): Promise<CryptoKey> {
  const raw = fromB64(keyB64);
  if (raw.length !== 32) throw new Error('FIELD_KEY must be 32 bytes base64 (see npm run secrets:keygen)');
  return crypto.subtle.importKey('raw', raw as BufferSource, AES_ALGO, false, ['encrypt', 'decrypt']);
}

/**
 * Envelope format:  v1:<iv_b64>:<ciphertext_b64>
 * Versioned so key rotation can introduce v2 without touching old rows.
 */
export async function encryptField(plaintext: string, keyB64: string): Promise<string> {
  const key = await importFieldKey(keyB64);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: AES_ALGO, iv: iv as BufferSource },
    key,
    enc.encode(plaintext) as BufferSource,
  );
  return `v1:${toB64(iv)}:${toB64(ct)}`;
}

export async function decryptField(envelope: string, keyB64: string): Promise<string> {
  const [v, ivB64, ctB64] = envelope.split(':');
  if (v !== 'v1') throw new Error(`Unsupported ciphertext version: ${v}`);
  const key = await importFieldKey(keyB64);
  const pt = await crypto.subtle.decrypt(
    { name: AES_ALGO, iv: fromB64(ivB64) as BufferSource },
    key,
    fromB64(ctB64) as BufferSource,
  );
  return dec.decode(pt);
}

/* ------------------------------------------------------------------- HMAC JWS */

async function hmacKey(secret: string): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, [
    'sign',
    'verify',
  ]);
}

export interface SessionClaims {
  sub: string;        // user id
  org: string;
  gr?: string | null; // global role
  pr?: Array<{ s: string; r: string }>;
  sid: string;        // session id (jti) — revocable
  iat: number;
  exp: number;
  dev?: string;
}

export async function signJwt(claims: SessionClaims, secret: string): Promise<string> {
  const header = toB64Url(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
  const payload = toB64Url(JSON.stringify(claims));
  const data = `${header}.${payload}`;
  const sig = await crypto.subtle.sign('HMAC', await hmacKey(secret), enc.encode(data) as BufferSource);
  return `${data}.${toB64Url(sig)}`;
}

export async function verifyJwt(token: string, secret: string): Promise<SessionClaims | null> {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const [h, p, s] = parts;
  const ok = await crypto.subtle.verify(
    'HMAC',
    await hmacKey(secret),
    fromB64Url(s) as BufferSource,
    enc.encode(`${h}.${p}`) as BufferSource,
  );
  if (!ok) return null;
  const claims = JSON.parse(dec.decode(fromB64Url(p))) as SessionClaims;
  if (claims.exp * 1000 < Date.now()) return null;
  return claims;
}

/** TOTP (RFC 6238) — used for the optional staff 2FA and for bridge enrolment. */
export async function totp(secretBase32: string, at: number = Date.now(), step = 30, digits = 6): Promise<string> {
  const key = base32Decode(secretBase32);
  const counter = Math.floor(at / 1000 / step);
  const buf = new ArrayBuffer(8);
  const view = new DataView(buf);
  view.setUint32(0, Math.floor(counter / 2 ** 32));
  view.setUint32(4, counter % 2 ** 32);
  const ck = await crypto.subtle.importKey('raw', key as BufferSource, { name: 'HMAC', hash: 'SHA-1' }, false, ['sign']);
  const sig = new Uint8Array(await crypto.subtle.sign('HMAC', ck, buf));
  const offset = sig[sig.length - 1] & 0x0f;
  const code =
    ((sig[offset] & 0x7f) << 24) | (sig[offset + 1] << 16) | (sig[offset + 2] << 8) | sig[offset + 3];
  return String(code % 10 ** digits).padStart(digits, '0');
}

export function base32Decode(input: string): Uint8Array {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  const clean = input.toUpperCase().replace(/=+$/, '').replace(/\s/g, '');
  let bits = 0;
  let value = 0;
  const out: number[] = [];
  for (const ch of clean) {
    const idx = alphabet.indexOf(ch);
    if (idx === -1) continue;
    value = (value << 5) | idx;
    bits += 5;
    if (bits >= 8) {
      out.push((value >>> (bits - 8)) & 0xff);
      bits -= 8;
    }
  }
  return new Uint8Array(out);
}
